import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Shield, Building2, Crown, Map } from 'lucide-react';
export function NationalScalePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-736" data-mp="1azw12n0tdarb">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-737" data-mp="wagdffwkxlfu">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-738" data-mp="118hszqusr2mk">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-739" data-mp="1eycyufych8w2">
            <span className="w-8 h-px bg-teal" data-id="element-740" data-mp="1jy7mmamtpicw"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-741" data-mp="1jwwkef910inw">
              Scale
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-742" data-mp="3mkgqb70ftj2"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-743" data-mp="8kvomgdqvtyn">
            National Scale
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-744" data-mp="3b1sh9aus5256">
            At the national scale, NCJI is designed to support strategic
            planning for civic resilience, institutional strengthening, and
            leadership qualification. It helps governments and national
            institutions better understand the human capability patterns that
            shape social stability, public trust, workforce readiness,
            civil-service performance, and long-term governance capacity.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-745" data-mp="7ka5wtfstd00">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-746" data-mp="lkbaojz6p4kr">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-747" data-mp="3eea3eo50p18h">
            National systems often try to address social and institutional
            problems at the policy level without having a structured view of the
            underlying human layer driving those outcomes. NCJI helps create
            that view. It supports a broader understanding of how reasoning,
            manipulation resistance, emotional regulation, social cooperation,
            self-direction, and leadership responsibility interact with national
            resilience and institutional strength over time.
          </p>
        </motion.div>
      </section>

      {/* Strategic Planning for Civic Resilience */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-748" data-mp="27qv230luig45">
        <div className="max-w-4xl mx-auto" data-id="element-749" data-mp="2cp6973kracio">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-750" data-mp="2cpy7im5qmocn">
            <div className="flex items-center gap-4 mb-6" data-id="element-751" data-mp="1qh0yrx8m212i">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-752" data-mp="1qilsjkn3pfzb">
                <Shield size={24} data-id="element-753" data-mp="2o3zvg7zaz90e" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-754" data-mp="2j5evm9nyqrch">
                Strategic Planning for Civic Resilience
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-755" data-mp="2a508d4rfv26c">
              NCJI can help national systems better understand where civic
              resilience is strong and where it is weak. This includes patterns
              related to misinformation vulnerability, weak consequential
              thinking, low civic reliability, poor opportunity alignment, and
              broader instability in how populations process pressure and
              participate in public life. This makes the framework relevant for
              long-range resilience planning, not just short-term response.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Institutional Strengthening */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-756" data-mp="35daao4zkyn98">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-757" data-mp="35b7ltoa5z063">
          <div className="flex items-center gap-4 mb-6" data-id="element-758" data-mp="1kx6y422bxmz4">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-759" data-mp="1kvm4cenua82b">
              <Building2 size={24} data-id="element-760" data-mp="1p0bzu2el5kmp" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-761" data-mp="1heqb83rphpoa">
              Institutional Strengthening
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-762" data-mp="1cfww66wrtqm0">
            At the national level, strong institutions require more than policy
            design and administrative structure. They also depend on the
            readiness, capability, and leadership depth of the people operating
            inside them. NCJI can help identify capability gaps across public
            institutions, support civil-service modernization, and strengthen
            long-term thinking around public-sector development and
            institutional continuity.
          </p>
        </motion.div>
      </section>

      {/* Leadership Qualification */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-763" data-mp="fxqv8a4t8qoy">
        <div className="max-w-4xl mx-auto" data-id="element-764" data-mp="az7b8f9s1oz8">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-765" data-mp="ayebhy98jzql">
            <div className="flex items-center gap-4 mb-6" data-id="element-766" data-mp="19hzhd2sx7d57">
              <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-767" data-mp="1edet9p7dayyz">
                <Crown size={24} data-id="element-768" data-mp="hqa3lhul1d7p" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-769" data-mp="lpiw6jdvz51o">
                Leadership Qualification
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-770" data-mp="qr88e2cuhorc">
              As consequence rises, the quality of leadership matters more. NCJI
              is designed to become more rigorous at higher levels of
              responsibility, making it relevant for senior public roles,
              executive readiness, and leadership qualification frameworks. It
              can support a more structured understanding of whether individuals
              in high-consequence environments show the reasoning, stability,
              foresight, and responsibility required for larger authority.
            </p>
          </motion.div>
        </div>
      </section>

      {/* National Capability Mapping */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-771" data-mp="3cup6j4z59w48">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-772" data-mp="38w9dokgdttiw">
          <div className="flex items-center gap-4 mb-6" data-id="element-773" data-mp="2tzs5g23hov0k">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-774" data-mp="x4cg3h4ynpg8">
              <Map size={24} data-id="element-775" data-mp="s7m0sw0zcxvv" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-776" data-mp="10yk48jz7my7">
              National Capability Mapping
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-777" data-mp="5yqcf3oj9b2x">
            NCJI also supports a broader strategic view by helping identify
            aggregate patterns across regions, institutions, and public systems.
            This includes workforce-readiness trends, leadership-pipeline depth,
            opportunity bottlenecks, institutional fragility, and civic
            capability gaps. Over time, this can help nations build more
            coherent long-range strategies around resilience, development,
            modernization, and governance stability.
          </p>
        </motion.div>
      </section>
    </div>;
}